import { motion, useReducedMotion } from "framer-motion";
import { Lock } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { cn } from "@/lib/cn";
import { SPRING_PILL } from "@/lib/motion";

export type HomeTab = "recommended" | "offers";

/** The Recommended / Offers pill switch under the feed title. */
export function HomeTabs({
  tab,
  onChange,
  signedIn,
}: {
  tab: HomeTab;
  onChange: (tab: HomeTab) => void;
  signedIn: boolean;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  return (
    <div
      role="tablist"
      aria-label={t("HOME_TITLE")}
      className="mt-5 inline-flex rounded-full border border-white/[0.12] light:border-black/10 bg-zinc-900/70 light:bg-white/70 p-1 text-sm shadow-[0_1px_0_rgba(255,255,255,0.05)_inset] backdrop-blur sm:mt-6"
    >
      {(["recommended", "offers"] as const).map((id) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={tab === id}
          onClick={() => onChange(id)}
          className="relative rounded-full px-4 py-2 font-medium transition sm:py-1.5"
        >
          {tab === id && (
            <motion.span
              layoutId="home-tab-pill"
              transition={reduceMotion ? { duration: 0 } : SPRING_PILL}
              className="absolute inset-0 rounded-full bg-paper light:bg-ink shadow-[0_2px_10px_-2px_rgba(245,241,232,0.35)]"
              aria-hidden
            />
          )}
          <span
            className={cn(
              "relative z-10 inline-flex items-center gap-1.5 transition-colors",
              tab === id ? "text-zinc-900 light:text-zinc-50" : "text-zinc-400 light:text-zinc-600 hover:text-zinc-100 light:hover:text-zinc-900",
            )}
          >
            {/* Guests see a small lock on Offers — the tab still opens, onto
                the "free account unlocks this" panel below. */}
            {id === "offers" && !signedIn && (
              <>
                <Lock className="h-3 w-3" aria-hidden />
                <span className="sr-only">{t("OFFERS_LOCKED_TAB_HINT")}</span>
              </>
            )}
            {t(id === "recommended" ? "HOME_TAB_RECOMMENDED" : "HOME_TAB_OFFERS")}
          </span>
        </button>
      ))}
    </div>
  );
}
